import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [activeProfileId, setActiveProfileId] = useState(
    () => localStorage.getItem("df_active_profile") || null
  );
  const [loading, setLoading] = useState(true);

  const loadContext = useCallback(async () => {
    if (!user) {
      setCtx(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/me/context");
      setCtx(data);
      const stored = localStorage.getItem("df_active_profile");
      const valid = data.profiles.find((p) => p.id === stored);
      const next = valid ? stored : data.active_profile_id;
      setActiveProfileId(next || null);
      return data;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const selectProfile = useCallback((id) => {
    localStorage.setItem("df_active_profile", id);
    setActiveProfileId(id);
  }, []);

  const activeProfile =
    ctx?.profiles?.find((p) => p.id === activeProfileId) || ctx?.profiles?.[0] || null;
  const activeFamily =
    ctx?.families?.find((f) => f.id === activeProfile?.family_id) || null;
  const activeMembership =
    ctx?.memberships?.find((m) => m.family_id === activeProfile?.family_id) || null;

  return (
    <ProfileContext.Provider
      value={{
        ctx,
        loading,
        profiles: ctx?.profiles || [],
        activeProfile,
        activeFamily,
        activeMembership,
        activeProfileId: activeProfile?.id || null,
        selectProfile,
        reload: loadContext,
        can: (perm) => activeMembership?.permissions?.includes(perm),
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}
